import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import {
  parseFilenameFromContentDisposition,
  triggerBlobDownload,
} from '@/shared/lib/downloadBlob'
import type { PackagingItemsConsolidationFilter } from '@/shared/packagingItemsConsolidationFilter'
import { toCommaQueryParam } from './commaQueryParam'
import {
  PACKAGING_SUPPLIERS_KEY,
  type PackagingFieldCrossCheckIssueCode,
  type PackagingHeavyMetalsAuditVerdict,
  type PackagingSvhcAuditVerdict,
} from './packagingSuppliers'
import type { PageInfo } from './packedItemsOverview'

export type { PageInfo }

export type PackagingItemAuditStatus = 'not_audited' | 'clear' | 'flagged'

export type PackagingItemComplianceRedFlag =
  | 'HEAVY_METALS'
  | 'SUBSTANCES_OF_CONCERN'

export type PackagingItemAuditIssue = {
  code: PackagingFieldCrossCheckIssueCode
  reasoningEn: string
  reasoningZh: string
}

export type PackagingItemHeavyMetalsAuditStatus =
  | 'not_audited'
  | 'passed'
  | 'failed'
  | 'flagged'

export type PackagingItemHeavyMetalsAuditFilterValue =
  | PackagingItemHeavyMetalsAuditStatus
  | PackagingHeavyMetalsAuditVerdict

export type PackagingItemHeavyMetalsAuditLatest = {
  verdict:
    | 'TEST_REPORT_REDUCED_SUFFICIENTLY'
    | 'TEST_REPORT_PRESENT_NOT_REDUCED_SUFFICIENTLY'
    | 'TEST_REPORT_PRESENT_MATERIAL_MISMATCH'
    | 'TEST_REPORT_PRESENT_DEFICIENT'
    | 'STATEMENT'
    | 'STATEMENT_PRESENT_NOT_RELEVANT'
    | 'STATEMENT_IS_TEST_REPORT_REDUCED_SUFFICIENTLY'
    | 'STATEMENT_IS_TEST_REPORT_NOT_REDUCED_SUFFICIENTLY'
    | 'STATEMENT_IS_TEST_REPORT_MATERIAL_MISMATCH'
    | 'STATEMENT_IS_TEST_REPORT_NOT_RELEVANT'
    | 'TEST_REPORT_NOT_PRESENT'
  reasoningEn: string
  reasoningZh: string
  evidenceDocumentIds: string[]
  timestamp: string
}

export type PackagingItemSvhcAuditStatus =
  | 'not_audited'
  | 'passed'
  | 'failed'
  | 'flagged'

export type PackagingItemSvhcAuditFilterValue =
  | PackagingItemSvhcAuditStatus
  | PackagingSvhcAuditVerdict

export type PackagingItemSvhcAuditLatest = {
  verdict:
    | 'SVHC_TEST_REPORT_SVHC_NEGATIVE'
    | 'SVHC_TEST_REPORT_SVHC_POSITIVE'
    | 'SVHC_TEST_REPORT_INCONCLUSIVE'
    | 'SVHC_TEST_REPORT_NOT_PRESENT'
    | 'SVHC_STATEMENT'
    | 'SVHC_STATEMENT_PRESENT_NOT_RELEVANT'
  reasoningEn: string
  reasoningZh: string
  evidenceDocumentIds: string[]
  timestamp: string
}

export type PackagingItemsOverviewLinkedPackedItem = {
  id: string
  name: string
  sku: string
}

export type PackagingItemsOverviewMergedInto = {
  id: string
  name: string
}

export type PackagingItemsOverviewRow = {
  id: string
  name: string
  sku: string | null
  supplierId: string
  supplierName: string
  completedAt: string | null
  complianceRedFlags: PackagingItemComplianceRedFlag[]
  auditStatus: PackagingItemAuditStatus
  auditIssues: PackagingItemAuditIssue[]
  auditLastRunAt: string | null
  heavyMetalsAuditStatus: PackagingItemHeavyMetalsAuditStatus
  heavyMetalsAuditLatest: PackagingItemHeavyMetalsAuditLatest | null
  heavyMetalsAuditLastRunAt: string | null
  svhcAuditStatus: PackagingItemSvhcAuditStatus
  svhcAuditLatest: PackagingItemSvhcAuditLatest | null
  svhcAuditLastRunAt: string | null
  documentsIngestedCount: number
  documentsIngestableCount: number
  skuInBomList: boolean
  linkedPackedItems: PackagingItemsOverviewLinkedPackedItem[]
  mergedIntoPackagingItem: PackagingItemsOverviewMergedInto | null
  lastChangeHistoryAt: string | null
  lastChangeHistoryAtBySupplier: string | null
  lastChangeHistoryAtByLex: string | null
  lastChangeHistoryAtByOther: string | null
}

export type PackagingItemsOrgStats = {
  total: number
  inDraft: number
  complete: number
  completePercent: number
  withRedFlags: number
}

export type PackagingItemsOverviewResponse = {
  partsCatalogConfigured: boolean
  stats: PackagingItemsOrgStats
  items: PackagingItemsOverviewRow[]
  consolidationFilter: PackagingItemsConsolidationFilter
  pageInfo: PageInfo
}

export type PackagingItemsOverviewSortBy =
  | 'name'
  | 'sku'
  | 'supplier'
  | 'status'
  | 'complianceRedFlags'
  | 'audit'
  | 'lastUpdatedByUser'
  | 'lastUpdatedByLex'
  | 'heavyMetalsAudit'
  | 'svhcAudit'

export type PackagingItemsOverviewSortDir = 'asc' | 'desc'

export type PackagingItemsOverviewQuery = {
  page?: number
  pageSize?: number
  searchQuery?: string
  supplierId?: string
  consolidationFilter?: PackagingItemsConsolidationFilter
  complianceRedFlags?: PackagingItemComplianceRedFlag[]
  audit?: PackagingItemAuditStatus[]
  heavyMetalsAudit?: PackagingItemHeavyMetalsAuditFilterValue[]
  svhcAudit?: PackagingItemSvhcAuditFilterValue[]
  lastUpdated?: string
  sortBy?: PackagingItemsOverviewSortBy
  sortDir?: PackagingItemsOverviewSortDir
}

export type PackagingItemsOverviewExportQuery = Omit<
  PackagingItemsOverviewQuery,
  'page' | 'pageSize'
>

export type PackagingItemsOverviewExportResponse = {
  items: PackagingItemsOverviewRow[]
}

export function useListPackagingItemsOverview(
  query: PackagingItemsOverviewQuery = {},
  enabled = true
) {
  const consolidationFilter = query.consolidationFilter ?? 'not_consolidated'

  return useQuery<PackagingItemsOverviewResponse, Error>({
    queryKey: [
      PACKAGING_SUPPLIERS_KEY,
      'packaging-items-overview',
      { ...query, consolidationFilter },
    ],
    queryFn: async () => {
      const response = await apiClient.get<PackagingItemsOverviewResponse>(
        '/v1/packaging-suppliers/packaging-items-overview',
        {
          params: {
            page: query.page,
            pageSize: query.pageSize,
            q: query.searchQuery,
            supplierId: query.supplierId,
            consolidationFilter:
              consolidationFilter === 'not_consolidated'
                ? undefined
                : consolidationFilter,
            complianceRedFlags: toCommaQueryParam(query.complianceRedFlags),
            audit: toCommaQueryParam(query.audit),
            heavyMetalsAudit: toCommaQueryParam(query.heavyMetalsAudit),
            svhcAudit: toCommaQueryParam(query.svhcAudit),
            lastUpdated: query.lastUpdated,
            sortBy: query.sortBy,
            sortDir: query.sortDir,
          },
        }
      )
      return response.data
    },
    enabled,
    placeholderData: keepPreviousData,
  })
}

export async function downloadPackagingItemsOverviewExport(
  query: PackagingItemsOverviewExportQuery = {},
  fallbackFilename: string
): Promise<void> {
  const consolidationFilter = query.consolidationFilter ?? 'not_consolidated'
  const response = await apiClient.get<Blob>(
    '/v1/packaging-suppliers/packaging-items-overview/export',
    {
      params: {
        q: query.searchQuery,
        supplierId: query.supplierId,
        consolidationFilter:
          consolidationFilter === 'not_consolidated'
            ? undefined
            : consolidationFilter,
        complianceRedFlags: toCommaQueryParam(query.complianceRedFlags),
        audit: toCommaQueryParam(query.audit),
        heavyMetalsAudit: toCommaQueryParam(query.heavyMetalsAudit),
        svhcAudit: toCommaQueryParam(query.svhcAudit),
        lastUpdated: query.lastUpdated,
        sortBy: query.sortBy,
        sortDir: query.sortDir,
      },
      responseType: 'blob',
      timeout: 120_000,
    }
  )
  const filename = parseFilenameFromContentDisposition(
    response.headers['content-disposition'],
    fallbackFilename
  )
  triggerBlobDownload(response.data, filename)
}
